import { Button, Group, Modal, SegmentedControl, Select, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { TbFileImport } from 'react-icons/tb'
import { parseTaskCsv, type RowError } from '../lib/csv'
import { notifyError } from '../lib/notify'
import { BulkCsvForm } from './createtaskmodal/BulkCsvForm'
import { SingleTaskForm } from './createtaskmodal/SingleTaskForm'
import {
  api,
  type ProjectMemberOption,
  type ProjectOption,
  type TagListItem,
  type TaskKind,
  type TaskPriority,
} from './createtaskmodal/types'

export function CreateTaskModal({
  opened,
  onClose,
  projects,
  defaultProjectId,
  defaultKind = 'TASK',
  heading = 'Create Task',
  onSubmit,
  onBulkSubmit,
  loading,
  error,
  tagsByProject,
  resetSignal,
}: {
  opened: boolean
  onClose: () => void
  projects: ProjectOption[]
  defaultProjectId: string | null
  defaultKind?: TaskKind
  // Modal heading text (NOT the task title field). Defaults to "Create Task";
  // the kind-scoped boards pass "Create Ticket" / "Create Idea".
  heading?: string
  // Incremented by the parent after each successful create; a change clears the
  // form. Undefined/0 means "no success yet" so the form is left untouched.
  resetSignal?: number
  onSubmit: (body: {
    projectId: string
    title: string
    description: string
    kind: TaskKind
    priority: TaskPriority
    assigneeId: string | null
    startsAt: string | null
    dueAt: string | null
    estimateHours: number | null
    tagIds: string[]
    phaseId: string | null
  }) => void
  onBulkSubmit: (body: {
    projectId: string
    tasks: Array<{
      title: string
      description: string
      kind: string
      priority: string
      startsAt: string | null
      dueAt: string | null
      estimateHours: number | null
      assigneeEmail: string | null
      tagNames: string[]
      phaseName: string | null
    }>
  }) => void
  loading: boolean
  error?: string
  tagsByProject: TagListItem[]
}) {
  const [mode, setMode] = useState<'single' | 'bulk'>('single')
  const [projectId, setProjectId] = useState<string | null>(defaultProjectId)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<TaskKind>(defaultKind)
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM')
  const [assigneeId, setAssigneeId] = useState<string | null>(null)
  const [startsAt, setStartsAt] = useState<Date | null>(null)
  const [dueAt, setDueAt] = useState<Date | null>(null)
  const [estimateHours, setEstimateHours] = useState<number | string>('')
  const [tagIds, setTagIds] = useState<string[]>([])
  const [phaseId, setPhaseId] = useState<string | null>(null)
  const [csvText, setCsvText] = useState('')

  const phasesQ = useQuery({
    queryKey: ['phases', projectId, 'modal'],
    queryFn: () =>
      api<{ phases: Array<{ id: string; title: string; status: string }> }>(`/api/projects/${projectId}/phases`),
    enabled: !!projectId,
  })
  const projectTagsQ = useQuery({
    queryKey: ['tags', projectId, 'modal'],
    queryFn: () => api<{ tags: TagListItem[] }>(`/api/projects/${projectId}/tags`),
    enabled: !!projectId,
  })
  // Members of the selected project — the assignee picker is scoped to them so
  // a task is never assigned to someone without access to the project.
  const membersQ = useQuery({
    queryKey: ['project-members', projectId, 'modal'],
    queryFn: () =>
      api<{
        project: {
          members: Array<{ role: string; user: { id: string; name: string; image: string | null; blocked?: boolean } }>
        }
      }>(`/api/projects/${projectId}`),
    enabled: !!projectId,
  })
  // Blocked users can't log in, so they're never assignable — even if they're
  // still a project member record (block doesn't remove membership).
  const members: ProjectMemberOption[] = (membersQ.data?.project.members ?? [])
    .filter((m) => !m.user.blocked)
    .map((m) => ({
      id: m.user.id,
      name: m.user.name,
      role: m.role as ProjectMemberOption['role'],
      image: m.user.image,
    }))

  const parsed = useMemo(() => (csvText.trim() ? parseTaskCsv(csvText) : null), [csvText])
  const errorsByRow = useMemo(() => {
    const m = new Map<number, RowError[]>()
    if (!parsed) return m
    for (const e of parsed.errors) {
      if (e.index < 0) continue
      const list = m.get(e.index) ?? []
      list.push(e)
      m.set(e.index, list)
    }
    return m
  }, [parsed])
  const headerErrors = parsed?.errors.filter((e) => e.index < 0) ?? []
  const tagsForProject = projectTagsQ.data?.tags ?? tagsByProject.filter((t) => t.projectId === projectId)
  const knownTagNames = new Set(tagsForProject.map((t) => t.name))
  const unknownTagsByRow = useMemo(() => {
    const m = new Map<number, string[]>()
    if (!parsed) return m
    for (let i = 0; i < parsed.rows.length; i++) {
      const unknown = parsed.rows[i].tagNames.filter((n) => !knownTagNames.has(n))
      if (unknown.length) m.set(i, unknown)
    }
    return m
  }, [parsed, knownTagNames])
  const totalErrors =
    (parsed?.errors.length ?? 0) + Array.from(unknownTagsByRow.values()).reduce((a, b) => a + b.length, 0)

  const invalidRange = Boolean(startsAt && dueAt && dueAt < startsAt)
  const availableTags = tagsForProject

  const reset = () => {
    setTitle('')
    setDescription('')
    setKind(defaultKind)
    setAssigneeId(null)
    setStartsAt(null)
    setDueAt(null)
    setEstimateHours('')
    setTagIds([])
    setPhaseId(null)
    setCsvText('')
  }

  // Reset the form ONLY after a successful create (parent bumps `resetSignal`).
  // Closing the modal by Cancel/X/overlay deliberately keeps the entered values
  // so an accidental close doesn't wipe the user's work — they can reopen and
  // continue. A success closes the modal via the parent (opened → false) without
  // hitting the onClose handler, which is why we key off the signal, not `opened`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset is stable; run only when the success signal changes
  useEffect(() => {
    if (resetSignal !== undefined && resetSignal > 0) reset()
  }, [resetSignal])

  const handlePickFile = async (file: File | null) => {
    if (!file) return
    if (!/\.(csv|txt)$/i.test(file.name)) {
      notifyError(new Error('Hanya file .csv yang didukung'))
      return
    }
    setCsvText(await file.text())
  }

  const submitBulk = () => {
    if (!projectId || !parsed || totalErrors > 0 || parsed.rows.length === 0) return
    onBulkSubmit({
      projectId,
      tasks: parsed.rows.map((r) => ({
        title: r.title,
        description: r.description,
        kind: r.kind,
        priority: r.priority,
        startsAt: r.startsAt,
        dueAt: r.dueAt,
        estimateHours: r.estimateHours,
        assigneeEmail: r.assigneeEmail,
        tagNames: r.tagNames,
        phaseName: r.phaseTitle ?? null,
      })),
    })
  }

  return (
    <Modal
      opened={opened}
      onClose={() => {
        // Keep the entered values on a manual close (X / overlay / Cancel) so an
        // accidental close doesn't wipe the user's work — only a successful
        // create clears the form (via resetSignal).
        onClose()
      }}
      title={heading}
      size={mode === 'bulk' ? 'xl' : 'md'}
    >
      <Stack gap="sm">
        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v as 'single' | 'bulk')}
          data={[
            { value: 'single', label: 'Single' },
            { value: 'bulk', label: 'Bulk CSV' },
          ]}
        />
        <Select
          label="Project"
          data={projects.map((p) => ({ value: p.id, label: p.name }))}
          value={projectId}
          onChange={(v) => {
            setProjectId(v)
            setAssigneeId(null) // members differ per project
          }}
          required
        />
        {mode === 'single' ? (
          <SingleTaskForm
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            kind={kind}
            setKind={setKind}
            priority={priority}
            setPriority={setPriority}
            assigneeId={assigneeId}
            setAssigneeId={setAssigneeId}
            members={members}
            startsAt={startsAt}
            setStartsAt={setStartsAt}
            dueAt={dueAt}
            setDueAt={setDueAt}
            invalidRange={invalidRange}
            estimateHours={estimateHours}
            setEstimateHours={setEstimateHours}
            tagIds={tagIds}
            setTagIds={setTagIds}
            phaseId={phaseId}
            setPhaseId={setPhaseId}
            availableTags={availableTags}
            phases={phasesQ.data?.phases ?? []}
          />
        ) : (
          <BulkCsvForm
            csvText={csvText}
            onCsvTextChange={setCsvText}
            onPickFile={handlePickFile}
            parsed={parsed}
            errorsByRow={errorsByRow}
            unknownTagsByRow={unknownTagsByRow}
            headerErrors={headerErrors}
            totalErrors={totalErrors}
          />
        )}
        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          {mode === 'single' ? (
            <Button
              onClick={() =>
                projectId &&
                onSubmit({
                  projectId,
                  title: title.trim(),
                  description: description.trim(),
                  kind,
                  priority,
                  assigneeId,
                  startsAt: startsAt ? startsAt.toISOString() : null,
                  dueAt: dueAt ? dueAt.toISOString() : null,
                  estimateHours: typeof estimateHours === 'number' ? estimateHours : null,
                  tagIds,
                  phaseId,
                })
              }
              disabled={!projectId || !title.trim() || !description.trim() || invalidRange || loading}
              loading={loading}
            >
              Create
            </Button>
          ) : (
            <Button
              leftSection={<TbFileImport size={14} />}
              onClick={submitBulk}
              disabled={!projectId || !parsed || parsed.rows.length === 0 || totalErrors > 0 || loading}
              loading={loading}
            >
              Import {parsed && totalErrors === 0 ? `${parsed.rows.length} task` : ''}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
